import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { IconName } from '../../enums';
import { DropdownOption } from '../../interfaces';
import { InputLabelBaseComponent } from '../../theme';
import { UseState } from '../../types';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';

import {
  BottomSection,
  CloseButton,
  Container,
  InputContainer,
  OptionLabel,
  OptionRow,
  OptionsPopup,
  OptionsPopupTitle,
  OptionsRows,
  OptionButton,
  OptionButtonInner,
  TopSection,
  Value,
  StyledIcon,
  TopSectionInner,
} from './fullscreenDropdown.styled';
import { FullscreenDropdownProps } from './fullscreenDropdown.types';

export const FullscreenDropdown: React.FC<FullscreenDropdownProps> = (props: FullscreenDropdownProps): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const {
    activeValues,
    activeValuesNames,
    label,
    options,
    multiple,
    onChooseAnswers,
    disabled = false,
    className
  }: FullscreenDropdownProps = props;
  const [optionsPopupVisible, setOptionsPopupVisible]: UseState<boolean> = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions]: UseState<DropdownOption[]> = useState<DropdownOption[]>(activeValues ?? []);

  useEffect((): void => setSelectedOptions(activeValues ?? []), [activeValues]);

  useEffect(
    (): void => {
      if (activeValuesNames !== undefined) {
        const optionsNames: string[] = options.map((option: DropdownOption): string => option.name);
        const items: DropdownOption[] = activeValuesNames
          .filter((name: string): boolean => optionsNames.includes(name))
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map((name: string): DropdownOption => options.find((option: DropdownOption): boolean => option.name === name)!);
        
        setSelectedOptions(items ?? []);
      }
    },
    [activeValuesNames]
  );

  const onOptionClick: (option: DropdownOption) => void = (option: DropdownOption): void => {
    let optionsArray: DropdownOption[] = [...selectedOptions];
    if (multiple) {
      if (!optionsArray.find((item: DropdownOption) => item.name === option.name)) {
        optionsArray.push(option);
        setSelectedOptions(optionsArray);
      } else {
        optionsArray = optionsArray.filter((item: DropdownOption) => item.name !== option.name);
      }
      setSelectedOptions(optionsArray);
      onChooseAnswers(optionsArray);
    } else {
      setSelectedOptions([option]);
      onChooseAnswers([option]);
      setOptionsPopupVisible(false);
    }
  };

  const openOptionsPopup = (): void => {
    if (disabled) {
      setOptionsPopupVisible(false);
      return;
    }

    setOptionsPopupVisible(true);
  };

  return (
    <Container className={className}>
      <InputContainer onClick={openOptionsPopup} disabled={disabled} data-testid='input-container'>
        <InputLabelBaseComponent
          $isOnTop={!!selectedOptions.length}
          $disabled={disabled}
          data-testid='dropdown-label'
          $hasSmallerWidth={!selectedOptions.length}
        >
          {label}
        </InputLabelBaseComponent>
        <Value>
          {selectedOptions.map((option: DropdownOption): string => option.label).join(', ')}
        </Value>
        <StyledIcon name={IconName.ArrowDown15} size={20} />
      </InputContainer>
      <OptionsPopup $visible={optionsPopupVisible}>
        <TopSection>
          <TopSectionInner>
            <CloseButton onClick={(): void => setOptionsPopupVisible(false)}>
              <Icon name={IconName.LeftArrow} size={24} />
            </CloseButton>
            <OptionsPopupTitle>{label}</OptionsPopupTitle>
          </TopSectionInner>
        </TopSection>
        <BottomSection>
          <OptionsRows>
            {options.map((option: DropdownOption): JSX.Element => (
              <OptionRow
                onClick={(): void => onOptionClick(option)}
                key={option.name}
              >
                <OptionButton $theme={multiple ? 'square' : 'circular'}>
                  <OptionButtonInner
                    $theme={multiple ? 'square' : 'circular'}
                    $active={!!selectedOptions.find((item: DropdownOption) => item.name === option.name)}
                  />
                </OptionButton>
                <OptionLabel>{option.label}</OptionLabel>
              </OptionRow>
            ))}
          </OptionsRows>
          {multiple && (
            <Button
              label={t('onarte.ui.fullscreenDropdown.approveButtonLabel')}
              onClick={(): void => setOptionsPopupVisible(false)}
              fullWidth
            />
          )}
        </BottomSection>
      </OptionsPopup>
    </Container>
  );
};
